import { KnownSettingsNodeDtoType } from '@groundfloor-platform/mgmt-service-g-sdk';
import { useMemo } from 'react';
import { ProjectSettingsForm } from '../project-settings-form/form';
import { SettingsActionDispatcher } from '../../reducer/settings-tree.reducer';
import { DynamicForm } from '../form.types';
import { useCreateProjectForm } from '../project-settings-form/create-form.hook';
import { interceptForm } from './dynamic-form-interceptor';
import { useRegisterDynamicForm } from './dynamic-form-register.hook';
import { RelationshipType, SettingsFormState } from '../../reducer/settings-tree-reducer.types';
import { useCreateProjectModuleForm } from '../project-module-form/create-form.hook';
import { ProjectModuleForm } from '../project-module-form/form';
import { useCreateRegistrationPageForm } from '../registration-page-form/create-form.hook';
import { useCreateHubForm } from '../hub-settings-form/create-form.hook';
import { HubSettingsForm } from '../hub-settings-form/form';
import { useCreateAccessControlForm } from '../access-control-form/create-form.hook';
import { useCreateRelationshipForm } from '../relationship-form/create-form.hook';
import { RelationshipForm } from '../relationship-form/form';

/**
 * The useCreatorForm hook is used to determine the correct form schema for the creator based on the node type.
 * It takes any node type from the tree and the generic form data for that node and returns the correct form schema, ui schema and form data.
 * @param node
 * @param formData
 * @returns
 */
export function useDynamicCreatorForm(
  formState: SettingsFormState | null,
  dispatch: SettingsActionDispatcher
): DynamicForm | null {
  const entityMap = useMemo(
    () => ({
      createProject: formState?.nodeType === KnownSettingsNodeDtoType.Project ? formState.parentProjectId : undefined,
      createProjectModule:
        formState?.nodeType === KnownSettingsNodeDtoType.ProjectModule ? formState.parentProjectId : undefined,
      createRegistrationPage:
        formState?.nodeType === KnownSettingsNodeDtoType.RegistrationPage ? formState.parentProjectId : undefined,
      createHub: formState?.nodeType === KnownSettingsNodeDtoType.Hub ? formState.parentProjectId : undefined,
      createAccessControl:
        formState?.nodeType === KnownSettingsNodeDtoType.AccessControl ? formState.parentEntityId : undefined,
      createRelationship:
        formState?.nodeType === KnownSettingsNodeDtoType.ProjectLinkFolder ? formState.parentEntityId : undefined
    }),
    [formState?.nodeType, formState?.parentProjectId, formState?.parentEntityId]
  );

  const projectFormSchema = useCreateProjectForm(
    entityMap.createProject,
    formState?.parentNodeId,
    formState?.form as ProjectSettingsForm | undefined
  );
  const projectModuleFormSchema = useCreateProjectModuleForm(
    entityMap.createProjectModule,
    formState?.parentNodeId,
    formState?.form as ProjectModuleForm | undefined
  );
  const registrationPageFormSchema = useCreateRegistrationPageForm(
    entityMap.createRegistrationPage,
    formState?.parentNodeId
  );
  const hubFormSchema = useCreateHubForm(
    entityMap.createHub,
    formState?.parentNodeId,
    formState?.form as HubSettingsForm | undefined
  );
  const accessControlFormSchema = useCreateAccessControlForm(
    entityMap.createAccessControl,
    formState?.parentNodeType,
    formState?.parentNodeId
  );
  const accessedViaRelationshipFormSchema = useCreateRelationshipForm(
    entityMap.createRelationship,
    formState?.parentProjectId,
    formState?.parentNodeId,
    formState?.form as RelationshipForm | undefined,
    (formState?.data?.relationshipType as RelationshipType) ?? RelationshipType.Unknown
  );

  const dynamicForm = useMemo(() => {
    if (entityMap.createProject && formState?.nodeId) {
      return interceptForm(formState?.nodeId, projectFormSchema, formState, true, dispatch);
    }
    if (entityMap.createProjectModule && formState?.nodeId) {
      return interceptForm(formState?.nodeId, projectModuleFormSchema, formState, false, dispatch);
    }
    if (entityMap.createRegistrationPage && formState?.nodeId) {
      return interceptForm(formState?.nodeId, registrationPageFormSchema, formState, false, dispatch);
    }
    if (entityMap.createHub && formState?.nodeId) {
      return interceptForm(formState?.nodeId, hubFormSchema, formState, false, dispatch);
    }
    if (entityMap.createAccessControl && formState?.nodeId) {
      return interceptForm(formState?.nodeId, accessControlFormSchema, formState, false, dispatch);
    }
    if (entityMap.createRelationship && formState?.nodeId) {
      return interceptForm(formState?.nodeId, accessedViaRelationshipFormSchema, formState, false, dispatch);
    }
    return null;
  }, [
    entityMap.createProject,
    entityMap.createProjectModule,
    entityMap.createRegistrationPage,
    entityMap.createHub,
    entityMap.createAccessControl,
    entityMap.createRelationship,
    formState,
    projectFormSchema,
    dispatch,
    projectModuleFormSchema,
    registrationPageFormSchema,
    hubFormSchema,
    accessControlFormSchema,
    accessedViaRelationshipFormSchema
  ]);

  useRegisterDynamicForm(dynamicForm, formState, dispatch);

  return dynamicForm;
}
